import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Shield, Scale, DollarSign, Briefcase, CheckCircle2 } from "lucide-react";

const StakeholderValueProps = () => {
  const stakeholders = [
    {
      role: "Compliance Office",
      icon: Shield,
      color: "text-primary",
      problem: "NCAA violations risk millions in fines, scholarship reductions, and postseason bans",
      solution: "Institutional Defense Layer",
      benefits: [
        "Tamper-proof audit trail for NCAA investigations",
        "Automated conflict detection (institutional sponsors, boosters, prohibited categories)",
        "AI risk assessment on every deal before approval",
        "Title IX equity monitoring to prevent civil rights violations"
      ]
    },
    {
      role: "Athletic Department",
      icon: Briefcase,
      color: "text-primary",
      problem: "Managing hundreds of athlete deals while maintaining competitive recruiting",
      solution: "Operational Efficiency Platform",
      benefits: [
        "Reduce deal review time from 3 days to <30 minutes",
        "Automated approval workflows with coach/AD sign-off",
        "Real-time revenue sharing (CAPS) calculations post-settlement",
        "NIL Go export automation for state compliance deadlines"
      ]
    },
    {
      role: "Finance / Controller",
      icon: DollarSign,
      color: "text-primary",
      problem: "$22M+ revenue sharing requires precise tracking and 1099 tax compliance",
      solution: "Financial Control System",
      benefits: [
        "Automated 1099 generation for athlete NIL payments",
        "Revenue sharing pool management with gender equity tracking",
        "Fair Market Value (FMV) benchmarking against market rates",
        "Quarterly tax reminders to reduce athlete tax liability"
      ]
    },
    {
      role: "General Counsel / Legal",
      icon: Scale,
      color: "text-primary",
      problem: "Exposure to Title IX lawsuits, contract disputes, and NCAA infractions",
      solution: "Legal Risk Mitigation Infrastructure",
      benefits: [
        "Contract analysis with AI-powered red flag detection",
        "Cryptographic audit trail for lawsuit defense (SOC 2 compliant)",
        "Historical pattern detection for coordinated booster activity",
        "Jurisdiction-specific rule enforcement (state laws + conference rules)"
      ]
    }
  ];

  return (
    <section className="py-24 px-8 bg-muted/30">
      <div className="max-w-[1200px] mx-auto">
        <div className="text-center mb-16">
          <Badge variant="outline" className="mb-4 font-light">
            Not a Marketplace — Institutional Infrastructure
          </Badge>
          <h2 className="text-4xl font-light text-foreground mb-4">
            Built for Institutional Stakeholders, Not Athletes
          </h2>
          <p className="text-lg font-light text-muted-foreground max-w-[800px] mx-auto">
            NIL marketplaces (Opendorse, INFLCR) connect athletes to brands. AthleteNexus protects <span className="font-medium text-foreground">institutions</span> from compliance, legal, and financial risks.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {stakeholders.map((stakeholder, index) => {
            const Icon = stakeholder.icon;
            return (
              <Card key={index} className="p-8 border-border bg-background">
                <div className="flex items-start gap-4 mb-6">
                  <div className="p-3 rounded-lg bg-primary/10">
                    <Icon className={`w-6 h-6 ${stakeholder.color}`} />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-foreground mb-1">
                      {stakeholder.role}
                    </h3>
                    <p className="text-sm text-muted-foreground font-light">
                      {stakeholder.problem}
                    </p>
                  </div>
                </div>

                <div className="mb-4">
                  <Badge variant="secondary" className="font-light">
                    {stakeholder.solution}
                  </Badge>
                </div>

                <ul className="space-y-3">
                  {stakeholder.benefits.map((benefit, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-foreground font-light leading-relaxed">
                        {benefit}
                      </span>
                    </li>
                  ))}
                </ul>
              </Card>
            );
          })}
        </div>

        <div className="mt-12 p-8 bg-primary/5 border border-primary/20 rounded-lg">
          <h4 className="text-lg font-semibold text-foreground mb-3">
            The Critical Difference: Defense vs. Distribution
          </h4>
          <div className="grid md:grid-cols-2 gap-6 text-sm">
            <div>
              <p className="font-medium text-destructive mb-2">❌ NIL Marketplaces (Opendorse, INFLCR, NIL Network)</p>
              <ul className="space-y-1 text-muted-foreground font-light">
                <li>• Focus: Athlete-brand matching</li>
                <li>• Customer: Athletes seeking deals</li>
                <li>• Value: Deal volume, brand partnerships</li>
                <li>• Liability: Minimal institutional protection</li>
              </ul>
            </div>
            <div>
              <p className="font-medium text-primary mb-2">✅ AthleteNexus (Institutional Infrastructure)</p>
              <ul className="space-y-1 text-muted-foreground font-light">
                <li>• Focus: Compliance enforcement & audit defense</li>
                <li>• Customer: Athletic departments, compliance offices</li>
                <li>• Value: Risk mitigation, NCAA violation prevention</li>
                <li>• Liability: Tamper-proof institutional protection</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StakeholderValueProps;
